import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { Form as FormUntyped, Button } from "../../primary";
import styles from "./assign.module.scss";
import topArrow from "../../../static/images/toparrow.svg";
import downArrow from "../../../static/images/downarrow.svg";
import deleteIcon from "../../../static/images/delete-icon.png";
import plusIconWhite from "../../../static/images/plus-icon_white.svg";
import {
  setUpdateStatus,
  updateFormBuilderMediaList,
  upDateformDetailsAsync,
} from "../../../app/customFormSlice";
import { AddRule } from "./addRule";
import { AddRuleText } from "./addRuleText";
import { CloseWarning } from "../../secondary";
import { Confirmation } from "../../secondary/Confirmation/confirmation";
import warning from "../../../static/images/icon-check-danger.png";
import ImageIcon from "../../../static/images/products/image_icon.svg";
import CloseIcon from "../../../static/images/products/close_icon.svg";
import Loader from "../../../static/images/spinner.svg";
import { toggleMandatory } from "./formBuilderUtils";
import { useNavigate } from "react-router-dom";
import { Toggle } from "./toggle";
import FormHeader from "./formHeader";
import SectionBlock from "./sectionBlock";
import FormFooter from "./formFooter";
import { QuestionBody } from "./questionBody";
import Tooltip from "../../secondary/Tooltip/tooltip";
import constants from "../../../constants/constants.json";
import UnSectionBlock from "./unSectionBlock";
import { PERMISSIONS } from "../../../constants/permissions";
import type {
  DragItem,
  DragOverTarget,
  FormBuilderOption,
  FormBuilderQuestion,
  FormBuilderSection,
  FormBuilderSectionWithQuestions,
  FormRule,
  ProductFamilyFilter,
} from "../../../features/custom-forms";
import type { AddOptionDropDownOption } from "./addOptionDropDown";

const Form = FormUntyped as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
  onSubmit: (...args: never[]) => unknown;
  requiredPermission?: string;
}>;

interface RuleIndex {
  pIndex: number;
  index?: number;
}

export const FormBuilder: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, errors, reset } = useForm({
    mode: "onChange",
  });

  const formData = useSelector((state: any) => state.forms.formDetailsData);
  const upDateStatus = useSelector((state: any) => state.forms.upDateStatus);
  const isFormLoading = useSelector((state: any) => state.forms.isFormLoading);

  const [sectionInfo, setSectionInfo] = useState<FormBuilderSection[]>([]);
  const [customFormBuilder, setcustomFormBuilder] = useState<FormBuilderQuestion[]>([]);
  const [getRule, setRule] = useState(false);
  const [ruleList, setRuleList] = useState<FormBuilderQuestion[]>([]);
  const [ruleEmpty, setRuleEmpty] = useState("");
  const [branch, setBranch] = useState(false);
  const [addBranchIndex, setBranchIndex] = useState<number | null>(null);
  const [modalShowRule, setmodalShowRule] = useState(false);
  const [getIndex, setIndex] = useState<Partial<RuleIndex>>({});
  const [editRule, setEditRule] = useState<FormRule | undefined>();
  const [showQuestionError, setQuestionError] = useState<{ index?: number } | undefined>();
  // `deleteInd` is `""` (not a number) until a real delete is in flight -- preserved as-is;
  // the only read site casts back to number, matching the real runtime value at that point.
  const [deleteInd, setDeleteInd] = useState<number | "">("");
  const [deleteIndRule, setDeleteIndRule] = useState(false);
  const [getRuleIndex, setGetRuleIndex] = useState<Array<number | string>>([]);
  const [modalShow, setmodalShow] = useState(false);
  const [closeWarning, setCloseWarning] = useState(false);
  const [addMedia, setAddMedia] = useState<{ index?: number } | undefined>();
  const [deleteQuestionCon, setDeleteQuestionCon] = useState(false);

  const [dragOverTarget, setDragOverTarget] = useState<DragOverTarget>({
    sectionIndex: null,
    questionIndex: null,
  });

  const sectionRefs = useRef<Array<HTMLElement | null>>([]);
  const unSectionRefs = useRef<Array<HTMLElement | null>>([]);
  const dropRef = useRef<HTMLElement>(null);
  const dragItemRef = useRef<DragItem | null>(null);

  const SCROLL_EDGE_OFFSET = 80;
  const SCROLL_SPEED = 20;

  const handleAutoScroll = (e: React.DragEvent) => {
    const { clientY } = e;
    const viewportHeight = window.innerHeight;

    if (clientY < SCROLL_EDGE_OFFSET) {
      window.scrollBy({ top: -SCROLL_SPEED, behavior: "auto" });
    } else if (clientY > viewportHeight - SCROLL_EDGE_OFFSET) {
      window.scrollBy({ top: SCROLL_SPEED, behavior: "auto" });
    }
  };

  const getQuestionIndex = useCallback(
    (tempId: string | undefined) => {
      return customFormBuilder.findIndex((q) => q.tempId === tempId);
    },
    [customFormBuilder]
  );

  const handleToggle = (index: number) => {
    setcustomFormBuilder((prev) => toggleMandatory(prev, index));
  };

  const handleSectionToggle = (index: number) => {
    const updatedSections = toggleMandatory(sectionInfo, index);

    const isSectionMandatory = updatedSections[index].isMandatory === 1;

    const updatedQuestions = customFormBuilder.map((q) =>
      updatedSections[index].questionsId.includes(q.tempId as string | number)
        ? {
            ...q,
            isMandatory: isSectionMandatory ? (0 as const) : q.isMandatory,
          }
        : q
    );
    setSectionInfo(updatedSections);
    setcustomFormBuilder(updatedQuestions);
  };

  const sectionedQuestionIds = useMemo(() => {
    return new Set(sectionInfo.flatMap((sec) => sec.questionsId));
  }, [sectionInfo]);

  const unSectionedQuestions = useMemo(() => {
    return customFormBuilder.filter((q) => !sectionedQuestionIds.has(q.tempId as string | number));
  }, [customFormBuilder, sectionedQuestionIds]);

  const prevSectionLength = useRef(sectionInfo.length);
  const prevUnSectionLength = useRef(unSectionedQuestions.length);

  useEffect(() => {
    if (sectionInfo.length > prevSectionLength.current) {
      const newSectionIndex = sectionInfo.length - 1;

      sectionRefs.current[newSectionIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    prevSectionLength.current = sectionInfo.length;
  }, [sectionInfo.length]);

  useEffect(() => {
    if (unSectionedQuestions.length > prevUnSectionLength.current) {
      const newUnsectionIndex = unSectionedQuestions.length - 1;

      unSectionRefs.current[newUnsectionIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    prevUnSectionLength.current = unSectionedQuestions.length;
  }, [unSectionedQuestions.length]);

  useEffect(() => {
    reset({});

    const timeoutId = setTimeout(() => {
      dispatch(setUpdateStatus({}));
    }, 1000);

    // productCategoryListOptions/getMateriallist pre-warm removed: singleSelect.js now
    // fetches this data itself via src/features/product-selector query hooks, which
    // dedupe/cache across every mounted consumer (Custom Forms migration Ticket 0).

    const builder: FormBuilderQuestion[] =
      formData?.formBuilderInfo?.length > 0
        ? [...formData.formBuilderInfo]
            .sort((a: FormBuilderQuestion, b: FormBuilderQuestion) => (a.order ?? 0) - (b.order ?? 0))
            .map((q: FormBuilderQuestion) => ({
              ...q,
              // Preserved as-is: existing questions keep a *numeric* id as their tempId
              // (not stringified) -- only brand-new questions get a real UUID string.
              tempId: (q.id > 0 ? q.id : crypto.randomUUID()) as unknown as string,
            }))
        : [];

    setcustomFormBuilder(builder);

    const sections: FormBuilderSection[] =
      formData?.sectionInfo?.length > 0
        ? formData.sectionInfo.map((sec: FormBuilderSection) => ({
            name: sec.name,
            sectionPlaceholder: sec.sectionPlaceholder,
            sectionId: sec.sectionId,
            isMandatory: sec.isMandatory,
            questionsId: sec.questionsId ?? [],
          }))
        : [];

    setSectionInfo(sections);

    return () => clearTimeout(timeoutId);
  }, [formData, reset, dispatch]);

  const addRuleButton = useCallback(() => {
    handleAddMediaCancel();
    setRule(false);

    dispatch(upDateformDetailsAsync({ ...formData, formBuilderInfo: customFormBuilder }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formData, customFormBuilder]);

  const handleDeleteImage = (
    imagesList: Array<{ image: string; fileName: string | null }> | undefined,
    formId: number | undefined,
    questionId: number | undefined,
    questionIndex: number,
    indexImg: number
  ) => {
    const filtered = Array.isArray(imagesList) && imagesList.filter((_data, i) => i !== indexImg);
    let list: { images: unknown[] } = { images: [] };
    if (filtered && filtered.length > 0) {
      list["images"] = [...filtered];
    }
    dispatch(
      updateFormBuilderMediaList({ formId, questionId, list, questionIndex, customFormBuilder, setcustomFormBuilder, setSectionInfo } as unknown as Parameters<
        typeof updateFormBuilderMediaList
      >[0])
    );
    handleAddMediaCancel();
  };

  const onSubmit = () => {
    handleAddMediaCancel();

    const feature = formData?.assignInfo?.feature;
    const isNE1WMS = feature === "NE1 WMS";

    let hasError = false;
    let saveRuleOption = false;

    const scrollToError = (index: number, focusInput = false) => {
      setQuestionError({ index });
      const errEl = document.querySelector(`.err_val${index}`);
      errEl?.scrollIntoView({ behavior: "smooth" });
      if (focusInput) {
        (errEl?.querySelector("input[type='text']") as HTMLElement | null)?.focus();
      }
    };

    for (let index = 0; index < customFormBuilder.length; index++) {
      const item = customFormBuilder[index];
      if (!item?.desc) {
        hasError = true;
        scrollToError(index, true);
        break;
      }
      if (item.goto === "eof" && !isNE1WMS) {
        if (item.rule === null) {
          setRuleEmpty("One / more End of form question rule not configured");
          hasError = true;
          scrollToError(index);
          break;
        }
        if (item.rule === undefined) {
          saveRuleOption = true;
        }
      }

      for (const opt of item?.options || []) {
        if (!opt.value) {
          hasError = true;
          scrollToError(index);
          break;
        }

        if (opt.goto === "eof" && !isNE1WMS) {
          if (opt.rule === null && item.id !== 0) {
            setRuleEmpty("One / more End of form question rule not configured");
            hasError = true;
            scrollToError(index);
            break;
          }
          if (opt.rule === undefined) {
            saveRuleOption = true;
          }
        }
      }

      if (hasError) break;
    }

    if ((document.querySelector(".err-msg-product") as HTMLElement | null)?.offsetTop !== undefined) {
      hasError = true;
    }

    if (getRuleIndex.length > 0) {
      hasError = true;
    }

    if (hasError) return;

    setQuestionError({});

    if (saveRuleOption && !isNE1WMS) {
      setRule(true);
      setmodalShow(true);
      setUpdateStatus({});
      return;
    }

    setRuleEmpty("");

    const tempIdToQuestionMap = new Map(customFormBuilder.map((q) => [q.tempId, q]));

    const tempIdToSectionMap = new Map<string | number | undefined, FormBuilderSection>();
    sectionInfo.forEach((sec) => {
      sec.questionsId?.forEach((tempId) => {
        tempIdToSectionMap.set(tempId, sec);
      });
    });

    const result = {
      ...formData,
      formBuilderInfo: customFormBuilder.map((q) => {
        const section = q.tempId ? tempIdToSectionMap.get(q.tempId) : null;

        return {
          ...q,
          tempId: undefined,
          sectionName: section?.name ?? null,
          sectionId: section?.sectionId ?? null,
        };
      }),

      sectionInfo: sectionInfo.map((sec) => ({
        ...sec,
        sectionId: sec?.sectionId ?? null,
        sectionPlaceholder: sec?.sectionPlaceholder ?? null,
        questionsId: sec.questionsId.map((tempId) => tempIdToQuestionMap.get(tempId as string)?.id ?? 0),
      })),
    };

    dispatch(upDateformDetailsAsync(result));
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickOutside = (e: MouseEvent) => {
    if (dropRef !== null && dropRef.current && !dropRef.current.contains(e.target as Node)) {
      setBranch(false);
    }
  };

  const addBranch = (index: number) => {
    handleAddMediaCancel();
    setBranch(true);
    setBranchIndex(index);
  };

  // select question type
  const selectAnswerType = (value: string, index: number) => {
    handleAddMediaCancel();
    if (!value) return;

    const makeDefaultOptions = (): FormBuilderOption[] => [
      { key: "1", value: "", order: 1, goto: "" },
      { key: "2", value: "", order: 2, goto: "" },
    ];

    setcustomFormBuilder((prev) => {
      if (index < 0 || index >= prev.length) return prev;

      const updated = [...prev];
      const isText = value === "text";

      updated[index] = {
        ...updated[index],
        type: value as FormBuilderQuestion["type"],
        goto: "",
        options: isText ? [] : makeDefaultOptions(),
      };

      return updated;
    });
  };

  // add branch
  const addBranchValue = (branchValue: string, pIndex: number) => {
    setcustomFormBuilder((prev) => {
      const next = [...prev];
      const question = { ...next[pIndex] };

      const isAddForOptions = branchValue === "Add for options";

      question.goto = isAddForOptions ? "" : "next";
      delete question.rule;

      question.options = (question.options || []).map((opt) => {
        const updatedOpt = { ...opt };
        updatedOpt.goto = isAddForOptions ? "next" : "";
        delete updatedOpt.rule;
        return updatedOpt;
      });

      next[pIndex] = question;
      return next;
    });

    setBranch(false);
  };

  // add new answer based on type
  const addNewQuestionOption = (value: AddOptionDropDownOption, _type: string, parentIndex: number) => {
    handleAddMediaCancel();
    let customFormObject = [...customFormBuilder];
    let selectTypeDat = [...customFormObject[parentIndex].options];

    let addNewQuestionOptionVal: Record<string, string> = {
      "1": `Option${selectTypeDat.length + 1}`,
      "2": "Others",
      "3": "All of the above",
      "4": "None of the above",
    };
    let addNewQuestionOptionKey: Record<string, string> = {
      "1": `Option${selectTypeDat.length + 1}`,
      "2": "other",
      "3": "alloftheabove",
      "4": "noneoftheabove",
    };

    if (
      customFormObject[parentIndex] &&
      customFormObject[parentIndex]?.optionType &&
      customFormObject[parentIndex]?.optionType === "productattribute"
    ) {
      selectTypeDat = [
        ...selectTypeDat,
        {
          order: (selectTypeDat.length + 1) * 1,
          key: `${value.key !== "1" ? addNewQuestionOptionKey[value.key] : selectTypeDat.length + 1}`,
          value: value.key !== "1" ? addNewQuestionOptionVal[value.key] : "",
          goto:
            selectTypeDat[0] && selectTypeDat[0].goto !== null && selectTypeDat[0].goto !== undefined && selectTypeDat[0].goto !== "" ? "next" : "",
          attributeValueKey: 0,
        },
      ];
    } else {
      selectTypeDat = [
        ...selectTypeDat,
        {
          order: (selectTypeDat.length + 1) * 1,
          key: `${value.key !== "1" ? addNewQuestionOptionKey[value.key] : selectTypeDat.length + 1}`,
          value: value.key !== "1" ? addNewQuestionOptionVal[value.key] : "",
          goto:
            selectTypeDat[0] && selectTypeDat[0].goto !== null && selectTypeDat[0].goto !== undefined && selectTypeDat[0].goto !== "" ? "next" : "",
        },
      ];
    }
    let addAnswer = { ...customFormObject[parentIndex] };
    addAnswer.options = selectTypeDat;
    let other = addAnswer?.options?.find((o) => o.value === "Others");
    let alloftheabove = addAnswer?.options?.find((o) => o.value === "All of the above");
    let noneoftheabove = addAnswer?.options?.find((o) => o.value === "None of the above");
    addAnswer.addOption = [
      { key: "1", value: "Add answer", visible: 1 as const },
      { key: "2", value: "Add other", visible: (other ? 0 : 1) as 0 | 1 },
      { key: "3", value: "Add all of the above", visible: (alloftheabove ? 0 : 1) as 0 | 1 },
      { key: "4", value: "Add none of the above", visible: (noneoftheabove ? 0 : 1) as 0 | 1 },
    ];
    let customFormObj = [...customFormObject];
    customFormObj.splice(parentIndex, 1, addAnswer);
    setcustomFormBuilder(customFormObj);
  };

  // deleteAnswerOption
  const deleteAnswerOption = (index: number, pIndex: number) => {
    handleAddMediaCancel();
    reset({});

    setcustomFormBuilder((prev) => {
      const next = [...prev];
      const question = { ...next[pIndex] };

      const options = (question.options || []).length > 2 ? question.options.filter((_, i) => i !== index) : [...(question.options || [])];

      const hasOther = options.some((o) => o.value === "Others");
      const hasAll = options.some((o) => o.value === "All of the above");
      const hasNone = options.some((o) => o.value === "None of the above");

      question.options = options;
      question.addOption = [
        { key: "1", value: "Add answer", visible: 1 as const },
        { key: "2", value: "Add other", visible: (hasOther ? 0 : 1) as 0 | 1 },
        { key: "3", value: "Add all of the above", visible: (hasAll ? 0 : 1) as 0 | 1 },
        { key: "4", value: "Add none of the above", visible: (hasNone ? 0 : 1) as 0 | 1 },
      ];

      next[pIndex] = question;
      return next;
    });
  };

  // up arrow sequence
  const upArrowSequence = (tempId: string | number | undefined, sectionIndex: number | null = null) => {
    handleAddMediaCancel();
    reset({});
    setcustomFormBuilder((prev) => {
      const updated = [...prev];
      if (sectionIndex !== null) {
        const section = sectionInfo[sectionIndex];
        const ids = [...section.questionsId];

        const pos = ids.indexOf(tempId as string | number);
        if (pos <= 0) return prev;

        [ids[pos - 1], ids[pos]] = [ids[pos], ids[pos - 1]];

        setSectionInfo((prevSecs) => prevSecs.map((sec, idx) => (idx === sectionIndex ? { ...sec, questionsId: ids } : sec)));

        return updated.map((q, i) => ({
          ...q,
          order: i + 1,
        }));
      }

      const unsectioned = updated.filter((q) => !sectionedQuestionIds.has(q.tempId as string | number));

      const pos = unsectioned.findIndex((q) => q.tempId === tempId);
      if (pos <= 0) return prev;
      const a = unsectioned[pos - 1];
      const b = unsectioned[pos];

      const idxA = updated.findIndex((q) => q.tempId === a.tempId);
      const idxB = updated.findIndex((q) => q.tempId === b.tempId);

      [updated[idxA], updated[idxB]] = [updated[idxB], updated[idxA]];

      return updated.map((q, i) => ({
        ...q,
        order: i + 1,
      }));
    });
  };

  // down arrow sequence
  const downArrowSequence = (tempId: string | number | undefined, sectionIndex: number | null = null) => {
    handleAddMediaCancel();
    reset({});

    setcustomFormBuilder((prev) => {
      const updated = [...prev];

      if (sectionIndex !== null) {
        const section = sectionInfo[sectionIndex];
        const ids = [...section.questionsId];

        const pos = ids.indexOf(tempId as string | number);
        if (pos === -1 || pos === ids.length - 1) return prev;

        [ids[pos], ids[pos + 1]] = [ids[pos + 1], ids[pos]];

        setSectionInfo((prevSecs) => prevSecs.map((sec, idx) => (idx === sectionIndex ? { ...sec, questionsId: ids } : sec)));

        return updated.map((q, i) => ({
          ...q,
          order: i + 1,
        }));
      }

      const unsectioned = updated.filter((q) => !sectionedQuestionIds.has(q.tempId as string | number));

      const pos = unsectioned.findIndex((q) => q.tempId === tempId);
      if (pos === -1 || pos === unsectioned.length - 1) return prev;

      const a = unsectioned[pos];
      const b = unsectioned[pos + 1];

      const idxA = updated.findIndex((q) => q.tempId === a.tempId);
      const idxB = updated.findIndex((q) => q.tempId === b.tempId);

      [updated[idxA], updated[idxB]] = [updated[idxB], updated[idxA]];

      return updated.map((q, i) => ({
        ...q,
        order: i + 1,
      }));
    });
  };

  const getInputValue = (e: React.FocusEvent<HTMLInputElement>, pIndex: number) => {
    const value = e.target.value;
    setcustomFormBuilder((prev) => prev.map((item, index) => (index === pIndex ? { ...item, placeholder: value } : item)));
  };

  const getAttributeVal = (e: React.ChangeEvent<HTMLInputElement>, index: number, attribute: string) => {
    handleAddMediaCancel();
    e.stopPropagation();
    let customFormObject = [...customFormBuilder];
    let selectTypeDat: FormBuilderQuestion = { ...customFormObject[index] };

    const uncheck = (id: string) => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      if (el) el.checked = false;
    };

    if (attribute === "productmaterial") {
      uncheck("productfamily" + index + 1);
      uncheck("productattribute" + index + 1);
      uncheck("productfamily" + index + 0);
      uncheck("productattribute" + index + 0);
    }
    if (attribute === "productattribute") {
      uncheck("productfamily" + index + 1);
      uncheck("productmaterial" + index + 1);
      uncheck("productfamily" + index + 0);
      uncheck("productmaterial" + index + 0);
    }
    if (attribute === "productfamily") {
      uncheck("productattribute" + index + 1);
      uncheck("productmaterial" + index + 1);
      uncheck("productattribute" + index + 0);
      uncheck("productmaterial" + index + 0);
    }

    if (e.target.value === "Yes") {
      selectTypeDat.optionType = attribute as FormBuilderQuestion["optionType"];
      delete selectTypeDat.type;
      delete selectTypeDat.productAttributeFilter;

      if (attribute === "productfamily" || attribute === "productmaterial") {
        selectTypeDat.options = [
          {
            key: "1",
            value: "",
            order: 1,
          },
          {
            key: "2",
            value: "",
            order: 2,
          },
        ];
        selectTypeDat.type = "singleselect";
      }
      selectTypeDat.goto = "";
    } else {
      selectTypeDat.optionType = null;
      selectTypeDat.options = [];
      delete selectTypeDat.type;
      selectTypeDat.goto = "";
      const questionTypeEl = document.getElementById("questionType" + index) as HTMLSelectElement | null;
      if (questionTypeEl) {
        // Preserved verbatim: assigning "" (not a number) to `selectedIndex` is a legacy
        // quirk -- the DOM coerces it to 0, which is the behavior relied upon here.
        questionTypeEl.selectedIndex = "" as unknown as number;
      }
    }
    let customFormObj = [...customFormObject];
    customFormObj.splice(index, 1, selectTypeDat);
    setcustomFormBuilder(customFormObj);
  };

  const fetchValue = (index: unknown, res: unknown, productAttr: unknown) => {
    handleAddMediaCancel();
    reset({});

    const typedRes = res as { data?: { attributeValue?: unknown } } | undefined;
    const options = typedRes?.data?.attributeValue ?? "";

    setcustomFormBuilder((prev) =>
      prev.map((item, i) =>
        i === (index as number)
          ? {
              ...item,
              type: "singleselect" as const,
              productAttributeFilter: productAttr as FormBuilderQuestion["productAttributeFilter"],
              // `options` can legitimately be the fallback `""` here (not an array) when the
              // mutation returned no envelope -- preserved as-is, cast to satisfy the shared type.
              options: options as unknown as FormBuilderOption[],
            }
          : item
      )
    );
  };

  const productAttributeOrderChange = (orderType: "up" | "down", index: number, pIndex: number) => {
    handleAddMediaCancel();
    let customFormObject = [...customFormBuilder];
    let selectTypeDat = { ...customFormObject[pIndex] };
    let selectTypeDatOptions = [...selectTypeDat.options];
    if (index !== 0 && orderType === "up") {
      reset({});
      selectTypeDatOptions.splice(index - 1, 0, selectTypeDatOptions[index]);
      selectTypeDatOptions.splice(index + 1, 1);
      let orderChange = selectTypeDatOptions.map((value, index) => {
        let order = { ...value };
        order.order = (index + 1) * 1;
        return order;
      });
      selectTypeDat.options = orderChange;
      let customFormObj = [...customFormObject];
      customFormObj.splice(pIndex, 1, selectTypeDat);
      setcustomFormBuilder(customFormObj);
    }
    if (selectTypeDatOptions.length > 1 && selectTypeDatOptions.length - 1 !== index && orderType === "down") {
      reset({});
      selectTypeDatOptions.splice(index, 0, selectTypeDatOptions[index + 1]);
      selectTypeDatOptions.splice(index + 2, 1);
      let orderChange = selectTypeDatOptions.map((value, index) => {
        let order = { ...value };
        order.order = (index + 1) * 1;
        return order;
      });
      selectTypeDat.options = orderChange;
      let customFormObj = [...customFormObject];
      customFormObj.splice(pIndex, 1, selectTypeDat);
      setcustomFormBuilder(customFormObj);
    }
  };

  const addRuleData = (ruleValue: FormRule | undefined, pIndex: number, index?: number) => {
    handleAddMediaCancel();
    let customFormObject = [...customFormBuilder];
    const rule = customFormObject.slice(0, pIndex + 1);
    setRuleList(rule);
    setmodalShowRule(true);
    setIndex({ pIndex: pIndex, index: index });
    setEditRule(ruleValue);
    setRuleEmpty("");
    reset({});
  };

  const productFamilyCode = (code: string[], index: number, pIndex: number, filter: ProductFamilyFilter) => {
    let customFormObject = [...customFormBuilder];
    let selectTypeDat = { ...customFormObject[pIndex] };
    let opt = [...selectTypeDat.options];
    let optInd = { ...opt[index] };
    optInd.productFamily = code;
    optInd.productFilter = filter;
    opt.splice(index, 1, optInd);
    selectTypeDat.options = opt;
    let customFormObj = [...customFormObject];
    customFormObj.splice(pIndex, 1, selectTypeDat);
    setcustomFormBuilder(customFormObj);
  };
  const productMaterialCode = (code: string[], index: number, pIndex: number, _filter: unknown) => {
    let customFormObject = [...customFormBuilder];
    let selectTypeDat = { ...customFormObject[pIndex] };
    let opt = [...selectTypeDat.options];
    let optInd = { ...opt[index] };
    optInd.productMaterial = code;
    optInd.productFamily = [];
    optInd.productFilter = undefined;
    opt.splice(index, 1, optInd);
    selectTypeDat.options = opt;
    let customFormObj = [...customFormObject];
    customFormObj.splice(pIndex, 1, selectTypeDat);
    setcustomFormBuilder(customFormObj);
  };

  const getRuleData = (rule: FormRule) => {
    const pIndex = getIndex?.pIndex as number;
    const index = getIndex?.index;
    let customFormObject = [...customFormBuilder];
    let selectTypeDat = { ...customFormObject[pIndex] };
    if (index !== undefined) {
      let opt = [...selectTypeDat.options];
      let optInd = { ...opt[index] };
      optInd.rule = rule;
      opt.splice(index, 1, optInd);
      selectTypeDat.options = opt;
      selectTypeDat.rule = undefined;
    } else {
      selectTypeDat.rule = rule;
      let removeRule = [...selectTypeDat.options].map((value) => {
        let ruleOption = { ...value };
        ruleOption.rule = undefined;
        return ruleOption;
      });
      selectTypeDat.options = removeRule;
    }
    let customFormObj = [...customFormBuilder];
    customFormObj.splice(pIndex, 1, selectTypeDat);
    setcustomFormBuilder(customFormObj);
    if (getRuleIndex.includes(selectTypeDat?.id)) {
      let getRule = [...getRuleIndex];
      getRule.pop();
      setGetRuleIndex(getRule);
    }
  };

  const isQuestionValid = (question: FormBuilderQuestion | undefined) => {
    if (!question?.desc?.trim()) return false;
    if (question.type === "text") return true;
    return question.options?.some((opt) => opt.value?.trim());
  };

  const getLastQuestionToValidate = (sectionIndex: number | null) => {
    if (sectionIndex !== null) {
      const section = sectionInfo[sectionIndex];
      if (!section?.questionsId?.length) return null;

      const lastTempId = section.questionsId[section.questionsId.length - 1];

      return customFormBuilder.find((q) => q.tempId === lastTempId);
    }

    const unsectioned = customFormBuilder.filter((q) => !sectionedQuestionIds.has(q.tempId as string | number));

    return unsectioned[unsectioned.length - 1] || null;
  };

  const getLastQuestionForAddSection = () => {
    if (sectionInfo.length > 0) {
      const lastSection = sectionInfo[sectionInfo.length - 1];

      if (lastSection.questionsId.length > 0) {
        const lastTempId = lastSection.questionsId[lastSection.questionsId.length - 1];

        return customFormBuilder.find((q) => q.tempId === lastTempId) || null;
      }
    }

    const unsectioned = customFormBuilder.filter((q) => !sectionedQuestionIds.has(q.tempId as string | number));

    return unsectioned[unsectioned.length - 1] || null;
  };

  const nextQuestion = (sectionIndex: number | null = null) => {
    handleAddMediaCancel();
    const lastQuestion = getLastQuestionToValidate(sectionIndex);
    if (lastQuestion && !isQuestionValid(lastQuestion)) {
      setQuestionError({ index: getQuestionIndex(lastQuestion.tempId) });
      return;
    }
    setQuestionError({});

    setcustomFormBuilder((prev) => {
      const unsectioned = prev.filter((q) => !sectionedQuestionIds.has(q.tempId as string | number));

      const newQuestion: FormBuilderQuestion = {
        id: 0,
        tempId: crypto.randomUUID(),
        desc: "",
        coreDesc: "",
        sectionId: null,
        _sectionIndex: sectionIndex,
        order: sectionIndex === null ? unsectioned.length + 1 : (sectionInfo?.[sectionIndex]?.questionsId?.length ?? 0) + 1,
        options: [],
        optionType: null,
        media: {
          images: [],
          embedVideo: [],
        },
        isMandatory: 0,
      };

      if (sectionIndex !== null) {
        setSectionInfo((prevSections) =>
          prevSections.map((sec, idx) =>
            idx === sectionIndex
              ? {
                  ...sec,
                  questionsId: [...sec.questionsId, newQuestion.tempId as string],
                }
              : sec
          )
        );
      }

      return [...prev, newQuestion];
    });
  };

  const handleCancelBtn = () => {
    setCloseWarning(true);
    window.scrollTo(0, 0);
  };

  const getOptionValue = (value: string, index: number, pIndex: number) => {
    handleAddMediaCancel();

    setcustomFormBuilder((prev) => {
      if (pIndex < 0 || pIndex >= prev.length) return prev;

      const updated = [...prev];
      const question = updated[pIndex];
      const options = Array.isArray(question.options) ? [...question.options] : [];

      if (index < 0 || index >= options.length) return prev;

      options[index] = {
        ...options[index],
        key: String(index + 1),
        value: value || "",
      };

      updated[pIndex] = {
        ...question,
        options,
      };

      return updated;
    });
  };

  const getGotoValue = (value: string, pIndex: number, index: number | "" = "") => {
    handleAddMediaCancel();
    let customFormObject = [...customFormBuilder];
    let selectTypeDat = { ...customFormObject[pIndex] };
    if (index === "") {
      selectTypeDat.goto = value;
      delete selectTypeDat.rule;
    } else {
      selectTypeDat.goto = "";
      let optionsVal = { ...selectTypeDat.options[index] };
      optionsVal.goto = value !== "" ? value : "";
      delete optionsVal.rule;
      let addOptionsValue = [...selectTypeDat.options];
      addOptionsValue.splice(index, 1, optionsVal);
      selectTypeDat.options = addOptionsValue;
    }
    let customFormObj = [...customFormObject];
    customFormObj.splice(pIndex, 1, selectTypeDat);
    setcustomFormBuilder(customFormObj);
  };

  const getQutions = (value: string, index: number) => {
    const updateFormBuilder = [...customFormBuilder];
    updateFormBuilder[index] = { ...updateFormBuilder[index], desc: value || "" };
    setcustomFormBuilder(updateFormBuilder);
  };

  const getQutionsCoreDesc = (value: string, index: number) => {
    const updateFormBuilder = [...customFormBuilder];
    updateFormBuilder[index] = { ...updateFormBuilder[index], coreDesc: value || "" };
    setcustomFormBuilder(updateFormBuilder);
  };

  const deleteQuestion = (value: FormBuilderQuestion, index: number) => {
    handleAddMediaCancel();
    setDeleteIndRule(false);
    setDeleteInd(index);
    setDeleteQuestionCon(true);
    if (value && value.rule && value.rule !== null) {
      setDeleteIndRule(true);
    }
    if (value && value.options) {
      let chck_rule = 0;
      Array.isArray(value.options) &&
        value.options.map((valueOpt) => {
          if (valueOpt && valueOpt.rule && valueOpt.rule !== null) {
            chck_rule = 1;
          }
        });
      if (chck_rule === 1) {
        setDeleteIndRule(true);
      }
    }
    if (customFormBuilder && Array.isArray(customFormBuilder) && customFormBuilder.length > 0) {
      let chck_rule_all = 0;
      customFormBuilder.map((all_value) => {
        Array.isArray(all_value.options) &&
          all_value.options.map((valueOpt) => {
            if (value.optionType === "productattribute" && valueOpt && valueOpt.rule) {
              Array.isArray(valueOpt?.rule?.attribute) &&
                valueOpt?.rule?.attribute.map((value_attr) => {
                  if (value_attr === value.id) {
                    chck_rule_all = 1;
                  }
                });
            }
            if (value.optionType === "productfamily" && valueOpt && valueOpt.rule) {
              Array.isArray(valueOpt?.rule?.productQuestion) &&
                valueOpt?.rule?.productQuestion.map((value_family) => {
                  if (value_family === value.id) {
                    chck_rule_all = 1;
                  }
                });
            }
          });
      });
      if (chck_rule_all === 1) {
        setDeleteIndRule(true);
      }
    }
  };

  const deleteList = () => {
    handleAddMediaCancel();
    setDeleteQuestionCon(false);

    const currentList = [...customFormBuilder];
    const deletedQuestion = currentList[deleteInd as number];

    if (!deletedQuestion) return;
    const { tempId, id } = deletedQuestion;

    let quesId: number[] = [];
    currentList.forEach((item, pIndex) => {
      if ((deleteInd as number) < pIndex) {
        if (item?.rule?.attribute?.includes(id) || item?.rule?.productQuestion?.includes(id)) {
          quesId.push(item.id);
        }

        item?.options?.forEach((opt) => {
          if (opt?.rule?.attribute?.includes(id) || opt?.rule?.productQuestion?.includes(id)) {
            quesId.push(item.id);
          }
        });
      }
    });

    setGetRuleIndex(quesId);

    const updatedQuestions = currentList.filter((_, idx) => idx !== (deleteInd as number));

    setcustomFormBuilder(updatedQuestions);

    setSectionInfo((prevSections) =>
      prevSections
        .map((section) => ({
          ...section,
          questionsId: section.questionsId.filter((qid) => qid !== tempId),
        }))
        .filter((section) => section.questionsId.length > 0)
    );

    reset({});
  };

  const modalClose = () => {
    setmodalShow(false);
    setmodalShowRule(false);
    setDeleteQuestionCon(false);
  };

  let nextQuestionOption = customFormBuilder?.length - 1;
  let questionType = customFormBuilder[nextQuestionOption]?.type;

  const handleYes = () => {
    setCloseWarning(false);
    navigate("/admin/formBuilder");
  };
  /* No fn */
  const handleNo = () => {
    handleAddMediaCancel();
    setCloseWarning(false);
  };
  const handleAddMedia = (index: number) => {
    setAddMedia({ index: index });
  };
  const handleAddMediaCancel = () => {
    setAddMedia({});
  };

  const handleAddSection = () => {
    const lastQuestion = getLastQuestionForAddSection();
    if (lastQuestion && !isQuestionValid(lastQuestion)) {
      const errorIndex = getQuestionIndex(lastQuestion.tempId);
      setQuestionError({ index: errorIndex });
      return;
    }

    setQuestionError({});
    setSectionInfo((prev) => [
      ...prev,
      {
        name: `Section ${prev.length + 1}`,
        sectionPlaceholder: "",
        isMandatory: 0 as const,
        questionsId: [],
      },
    ]);
  };

  const questionMap = useMemo(() => {
    const map: Record<string, FormBuilderQuestion> = {};
    customFormBuilder.forEach((q) => {
      map[q?.tempId as string] = q;
    });
    return map;
  }, [customFormBuilder]);

  const sections: FormBuilderSectionWithQuestions[] = useMemo(() => {
    return sectionInfo.map((section) => ({
      name: section.name,
      sectionPlaceholder: section.sectionPlaceholder,
      isMandatory: section.isMandatory,
      questionsId: section.questionsId,
      questions: section.questionsId.map((id) => questionMap[id as string]).filter(Boolean),
    }));
  }, [sectionInfo, questionMap]);

  const handleSectionTitle = (value: string, sectionIndex: number) => {
    setSectionInfo((prevSections) =>
      prevSections.map((section, idx) =>
        idx === sectionIndex
          ? {
              ...section,
              sectionPlaceholder: value,
            }
          : section
      )
    );
  };

  const getQuestionIdsBySection = (sectionIndex: number | null): Array<string | number> => {
    if (sectionIndex === null) {
      return unSectionedQuestions.map((q) => q.tempId as string | number);
    }
    return sectionInfo[sectionIndex].questionsId;
  };

  const setQuestionIdsBySection = (sectionIndex: number | null, newIds: Array<string | number>) => {
    if (sectionIndex === null) {
      return;
    }

    setSectionInfo((prev) => prev.map((sec, idx) => (idx === sectionIndex ? { ...sec, questionsId: newIds } : sec)));
  };

  const moveQuestionBetweenSections = (
    fromSectionIndex: number | null,
    fromIndex: number,
    toSectionIndex: number | null,
    toIndex: number,
    tempId: string
  ) => {
    if (fromSectionIndex === toSectionIndex) return;
    const sourceIds = [...getQuestionIdsBySection(fromSectionIndex)];
    sourceIds.splice(fromIndex, 1);

    if (fromSectionIndex !== null) {
      setQuestionIdsBySection(fromSectionIndex, sourceIds);
    }

    if (toSectionIndex !== null) {
      const targetIds = [...getQuestionIdsBySection(toSectionIndex)];
      targetIds.splice(toIndex, 0, tempId);
      setQuestionIdsBySection(toSectionIndex, targetIds);
    }
  };

  const onDragStart = (fromSectionIndex: number | null, fromIndex: number, tempId: string) => {
    dragItemRef.current = {
      fromSectionIndex,
      index: fromIndex,
      tempId,
    };
  };

  const onDragOver = (e: React.DragEvent, sectionIndex: number | null, overIndex: number) => {
    e.preventDefault();
    handleAutoScroll(e);

    setDragOverTarget({
      sectionIndex,
      questionIndex: overIndex,
    });
  };

  const onDrop = (toSectionIndex: number | null, toIndex: number) => {
    const dragItem = dragItemRef.current;
    if (!dragItem) return;

    const { fromSectionIndex, index: fromIndex, tempId } = dragItem;

    if (fromSectionIndex === toSectionIndex && fromIndex === toIndex) {
      return;
    }

    moveQuestionBetweenSections(fromSectionIndex, fromIndex, toSectionIndex, toIndex, tempId);
    dragItemRef.current = null;
    setDragOverTarget({ sectionIndex: null, questionIndex: null });
  };

  const renderQuestion = (
    value: FormBuilderQuestion,
    isLastInGroup: boolean,
    groupIndex: number,
    sectionIndex: number | null = null,
    isMandatory = false
  ) => {
    const index = customFormBuilder.findIndex((item) => item.tempId === value?.tempId);
    return (
      <div
        className={styles.question_blk}
        key={value.tempId}
        draggable
        onDragStart={() => onDragStart(sectionIndex, groupIndex, value.tempId as string)}
        onDragOver={(e) => onDragOver(e, sectionIndex, groupIndex)}
        onDrop={() => onDrop(sectionIndex, groupIndex)}
      >
        {dragOverTarget.sectionIndex === sectionIndex && dragOverTarget.questionIndex === groupIndex && (
          <div className={styles.drop_placeholder}>Drop here...</div>
        )}

        <div className={`col-sm-12 ${styles.col_spec} ${styles.question_top} align-items-center`}>
          <p className={`${styles.question_heading}`}>Question {groupIndex + 1}</p>

          <div className={`verticalCode ${styles.vertical_Code}`}></div>

          <Toggle
            checked={value?.isMandatory === 1}
            type="question"
            index={index}
            onChange={handleToggle}
            customStyle="pt-2"
            disabled={isMandatory}
          />

          <div className={`col-10 w-100 ${styles.media_view}`}>
            <ul className={`${styles.list_view} ml-5`}>
              {value?.media?.images?.map((valueImg, indexImg) => (
                <li key={indexImg}>
                  <img src={ImageIcon} alt="image_icon" />
                  <a href={valueImg?.image} target="_blank" rel="noopener noreferrer" title={valueImg?.fileName ?? undefined}>
                    {valueImg?.fileName}
                  </a>
                  <span onClick={() => handleDeleteImage(value?.media?.images, formData?.id, value?.id, index, indexImg)} title="Remove">
                    <img src={CloseIcon} alt="close" />
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className={styles.up_down_list}>
            {groupIndex !== 0 && (
              <span className={`${styles.sequence_arrow} uparrow`} onClick={() => upArrowSequence(value?.tempId, sectionIndex)}>
                <img src={topArrow} alt="Top arrow" />
              </span>
            )}
            {!isLastInGroup && (
              <span className={`${styles.sequence_arrow} downarrow`} onClick={() => downArrowSequence(value?.tempId, sectionIndex)}>
                <img src={downArrow} alt="Down arrow" />
              </span>
            )}
            <span className={`${styles.sequence_arrow} deleteQues`} onClick={() => deleteQuestion(value, index)}>
              <img src={deleteIcon} alt="delete" />
            </span>
            <Tooltip placement="left" className="mt-2" content={constants.customForm.questionDragDrop}>
              <i className="fas" style={{ fontSize: "20px", color: "#787878" }}>
                &#xf58e;
              </i>
            </Tooltip>
          </p>
        </div>

        <div className={`${styles.form_block1} mb-2`}>
          <QuestionBody
            qvalue={value}
            pIndex={index}
            groupIndex={groupIndex}
            sectionIndex={sectionIndex}
            customFormBuilder={customFormBuilder}
            formData={formData}
            branch={branch}
            addBranchIndex={addBranchIndex}
            getRuleIndex={getRuleIndex}
            dropRef={dropRef}
            register={register as unknown as (...args: unknown[]) => unknown}
            errors={errors}
            showQuestionError={showQuestionError}
            getQutions={getQutions}
            getQutionsCoreDesc={getQutionsCoreDesc}
            selectAnswerType={selectAnswerType}
            getAttributeVal={getAttributeVal}
            fetchValue={fetchValue}
            getGotoValue={getGotoValue}
            getInputValue={getInputValue}
            addRuleData={addRuleData}
            addBranch={addBranch}
            addBranchValue={addBranchValue}
            addNewQuestionOption={addNewQuestionOption}
            addMedia={addMedia}
            handleAddMedia={handleAddMedia as unknown as (...args: unknown[]) => void}
            handleAddMediaCancel={handleAddMediaCancel}
            setcustomFormBuilder={setcustomFormBuilder}
            setSectionInfo={setSectionInfo}
            getOptionValue={getOptionValue}
            deleteAnswerOption={deleteAnswerOption}
            productFamilyCode={productFamilyCode as unknown as (...args: unknown[]) => void}
            productMaterialCode={productMaterialCode as unknown as (...args: unknown[]) => void}
            productAttributeOrderChange={productAttributeOrderChange as unknown as (...args: unknown[]) => void}
          />
        </div>

        {isLastInGroup && (
          <div className={styles.question_add_new} title="Add Next Question">
            <button type="button" className="btn btn-transparent next-ques" onClick={() => nextQuestion(sectionIndex)}>
              <img src={plusIconWhite} alt="Plus icon" />
            </button>
          </div>
        )}
      </div>
    );
  };

  if (isFormLoading) {
    return (
      <div className={`loader col-12`}>
        <img src={Loader} alt="loading" />
      </div>
    );
  }

  return (
    <>
      {closeWarning && (
        <div className="text-center">
          <CloseWarning
            message="All progress in this section will be lost. Are you sure want to cancel?"
            yesFn={handleYes}
            noFn={handleNo}
            accessError={undefined}
          />
        </div>
      )}
      {deleteQuestionCon && (
        <Confirmation onClose={() => modalClose()} title={" "}>
          <div className="text-center pr-5 pl-5">
            <p>
              <img src={warning} alt="warnning-icon" />
            </p>
            <p className="pb-3">Are you sure you'd like to delete Question.{deleteIndRule && <>This question affect the rule</>}</p>
            <div className="mb-2">
              <Button value="Yes" classes={{ className: `btn mr-2 pb-2 pt-2 mr-3 ${styles.yes_btn}` }} onClick={() => deleteList()} />
              <Button id="closeId" value="No" classes={{ className: `btn ml-2 pb-2 pt-2 ${styles.no_btn}` }} onClick={() => modalClose()} />
            </div>
          </div>
        </Confirmation>
      )}
      <>
        <FormHeader
          onAddSection={handleAddSection}
          onAddQuestion={nextQuestion}
          unSectionedQuestions={unSectionedQuestions}
        />
        <div className={`col-md-12 ${styles.single_product_block} formbuilder`}>
          {upDateStatus && upDateStatus.status === "success" && (
            <div className={`alert alert-success ${styles.form_alert} ${styles.sucessmsg}`}>Successfully updated</div>
          )}
          {upDateStatus && upDateStatus.status === "error" && <div className={`alert alert-danger ${styles.form_alert}`}>Form not update</div>}
          {ruleEmpty !== "" && <div className={`alert alert-danger ${styles.form_alert} ${styles.sucessmsg}`}>{ruleEmpty}</div>}
          <div className="row">
            <div className={`p-0 ${styles.patient_content}`}>
              <Form className={`pl-2 pt-0 ${styles.patient_content} formSubmit`} onSubmit={handleSubmit(onSubmit)} requiredPermission={PERMISSIONS.ADMIN_FORM_BUILDER}>
                {sections.map((section, sectionIndex) => (
                  <SectionBlock
                    key={sectionIndex}
                    section={section}
                    sectionIndex={sectionIndex}
                    renderQuestion={renderQuestion}
                    handleSectionTitle={handleSectionTitle}
                    handleSectionToggle={handleSectionToggle}
                    sectionRefs={sectionRefs}
                    errors={errors}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onDragStart={onDragStart}
                    dragOverTarget={dragOverTarget}
                    onAddQuestion={nextQuestion}
                    dragItemRef={dragItemRef}
                  />
                ))}

                <UnSectionBlock
                  unSectionedQuestions={unSectionedQuestions}
                  unSectionRefs={unSectionRefs}
                  renderQuestion={renderQuestion}
                  dragOverTarget={dragOverTarget}
                  dragItemRef={dragItemRef}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  sections={sections}
                />

                {sections?.length === 0 && unSectionedQuestions?.length === 0 && (
                  <div className={`col-12 ${styles.noSelection}`}>Start by adding a question or a section</div>
                )}

                {getRule && <AddRule modalOpen={modalShow} modalClose={modalClose} addRuleButton={addRuleButton} />}
                {
                  <AddRuleText
                    modalOpen={modalShowRule}
                    modalClose={modalClose}
                    addRuleList={ruleList}
                    getRuleData={getRuleData}
                    defaultValue={editRule}
                  />
                }
                {questionType !== undefined && <FormFooter show={questionType} onCancel={handleCancelBtn} />}
              </Form>
            </div>
          </div>
        </div>
      </>
    </>
  );
};
